import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';

interface KpiCardProps {
  /** Already-translated metric label (the card title). */
  label: ReactNode;
  /** The metric value, shown when not loading. Any node (number / string / element). */
  value: ReactNode;
  /** When true, the value slot shows a unified loading placeholder instead of
   *  the value — retiring the per-page inline `'...'` string (#1291). */
  loading?: boolean;
  /** Optional leading glyph rendered in the header, right-aligned. */
  icon?: ReactNode;
  /** Optional supporting copy under the value. */
  description?: ReactNode;
  /** Optional trend indicator rendered under the value (above description). */
  trend?: ReactNode;
  /** Renders the card as a router link (the card's only activation affordance). */
  to?: string;
  className?: string;
  /** Extra classes on the value element (e.g. a narrower size for text values). */
  valueClassName?: string;
  'data-testid'?: string;
}

/**
 * Canonical KPI tile (#1291). Collapses the near-identical stat cards that
 * were copied across DashboardPage / MonitoringPage / DispatchDashboard —
 * each a `Card > CardHeader(title + icon) > CardContent(text-2xl value +
 * description)` with a bespoke `'...'` inline loading string. Pages now
 * declare content; this component owns the layout AND the single loading
 * presentation.
 */
export function KpiCard({
  label,
  value,
  loading = false,
  icon,
  description,
  trend,
  to,
  className,
  valueClassName,
  'data-testid': dataTestid,
}: KpiCardProps) {
  const interactive = to != null;
  const card = (
    <Card
      data-testid={dataTestid}
      className={cn(
        'h-full',
        interactive && 'cursor-pointer transition-colors hover:border-primary/50',
        className,
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold', valueClassName)}>
          {loading ? (
            <span
              data-testid="kpi-loading"
              aria-hidden="true"
              className="inline-block h-7 w-16 animate-pulse rounded bg-muted align-middle"
            />
          ) : (
            value
          )}
        </div>
        {trend}
        {description != null && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );

  if (to != null) {
    return (
      <Link
        to={to}
        className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {card}
      </Link>
    );
  }
  return card;
}
